import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Auth } from '~/amplify';
import { AuthContext } from '~/Root/Auth';

export type CognitoPhoneAttributes = {
  phoneNumber: string | null;
  phoneVerified: boolean;
};

export type ProfileCognitoPhoneContextValue = CognitoPhoneAttributes & {
  loading: boolean;
  refresh: () => Promise<CognitoPhoneAttributes>;
};

const ProfileCognitoPhoneContext =
  createContext<ProfileCognitoPhoneContextValue | null>(null);

const mapAttributes = (
  attributes: Record<string, string | undefined>,
): CognitoPhoneAttributes => ({
  phoneNumber: attributes.phone_number ?? null,
  phoneVerified: attributes.phone_number_verified === 'true',
});

/**
 * Phone attributes for MFA settings. State is seeded from Root Auth for the first
 * paint, then refresh() loads the latest Cognito attributes when this provider
 * mounts (inside MFA settings). Call refresh() again after phone/MFA mutations.
 */
export const ProfileCognitoPhoneProvider: React.FCC = ({ children }) => {
  const { user: cognitoUser } = useContext(AuthContext);

  const [phoneAttributes, setPhoneAttributes] =
    useState<CognitoPhoneAttributes>({
      phoneNumber: cognitoUser?.attributes.phoneNumber || null,
      phoneVerified: cognitoUser?.attributes.phoneVerified || false,
    });
  const [loading, setLoading] = useState(true);

  const inFlightRefreshRef = useRef<Promise<CognitoPhoneAttributes> | null>(
    null,
  );

  const refresh = useCallback(async (): Promise<CognitoPhoneAttributes> => {
    const existing = inFlightRefreshRef.current;
    if (existing != null) {
      return existing;
    }

    const promise = (async (): Promise<CognitoPhoneAttributes> => {
      setLoading(true);
      try {
        const attributes = await Auth.fetchUserAttributes();
        const mappedAttributes = mapAttributes(attributes);
        setPhoneAttributes(mappedAttributes);
        return mappedAttributes;
      } finally {
        setLoading(false);
        inFlightRefreshRef.current = null;
      }
    })();

    inFlightRefreshRef.current = promise;
    return promise;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value: ProfileCognitoPhoneContextValue = {
    ...phoneAttributes,
    loading,
    refresh,
  };

  return (
    <ProfileCognitoPhoneContext.Provider value={value}>
      {children}
    </ProfileCognitoPhoneContext.Provider>
  );
};

export const useProfileCognitoPhone = (): ProfileCognitoPhoneContextValue => {
  const ctx = useContext(ProfileCognitoPhoneContext);
  if (ctx == null) {
    throw new Error(
      'useProfileCognitoPhone must be used within ProfileCognitoPhoneProvider',
    );
  }
  return ctx;
};

/**
 * Supplies fixed Cognito phone attributes for Storybook/tests (no Auth.fetchUserAttributes).
 */
export const MockProfileCognitoPhoneProvider: React.FCC<
  React.PropsWithChildren<{
    phoneNumber?: string | null;
    phoneVerified?: boolean;
    loading?: boolean;
    refresh?: () => Promise<CognitoPhoneAttributes>;
  }>
> = ({
  children,
  phoneNumber = null,
  phoneVerified = false,
  loading = false,
  refresh: refreshProp,
}) => {
  const defaultRefresh = useCallback(
    async () => ({ phoneNumber, phoneVerified }),
    [phoneNumber, phoneVerified],
  );

  const refresh = refreshProp ?? defaultRefresh;

  const value: ProfileCognitoPhoneContextValue = {
    phoneNumber,
    phoneVerified,
    loading,
    refresh,
  };

  return (
    <ProfileCognitoPhoneContext.Provider value={value}>
      {children}
    </ProfileCognitoPhoneContext.Provider>
  );
};
